import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";

export interface WordProcessorDataParams {
  openSelectSaleDialog?: boolean;
  openSelectSaleConfigurationDialog?: boolean;
  patientId?: number;
  saleId?: number;
  saleConfiguration?: Record<string, any>;
  showSilenceDialog?: boolean;
}

export interface AIPromptData {
  id: string;
  dialogOpen: boolean;
  prompt: string;
  result: string;
  isBusy: boolean;
  extra?: Record<string, any>;
}

interface WordProcessorState {
  aiPrompts: Record<string, Partial<AIPromptData>>;
  dataParams: Record<string, WordProcessorDataParams>;
}

const initialState: WordProcessorState = {
  aiPrompts: {},
  dataParams: {}
};

export const wordProcessorSlice = createSlice({
  name: "wordProcessor",
  initialState,
  reducers: {
    setAiPromptDetails: (
      state,
      action: PayloadAction<Partial<AIPromptData>>
    ) => {
      const { id, dialogOpen, prompt, result, isBusy, extra } = action.payload;
      if (!id) return;
      const newDetails = state.aiPrompts[id] || {};

      if (dialogOpen !== undefined) {
        newDetails.dialogOpen = dialogOpen;
      }
      if (prompt !== undefined) {
        newDetails.prompt = prompt;
      }
      if (result !== undefined) {
        newDetails.result = result;
      }
      if (isBusy !== undefined) {
        newDetails.isBusy = isBusy;
      }
      if (extra !== undefined) {
        newDetails.extra = extra;
      }
      state.aiPrompts[id] = newDetails;
    },
    clearAiPromptDetails: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!id) return;
      delete state.aiPrompts[id];
    },

    setDataParams: (
      state,
      action: PayloadAction<
        Partial<WordProcessorDataParams> & { id: string | undefined }
      >
    ) => {
      const {
        id,
        openSelectSaleDialog,
        openSelectSaleConfigurationDialog,
        patientId,
        saleId,
        saleConfiguration,
        showSilenceDialog
      } = action.payload;

      if (!id) return;

      const newParams = state.dataParams[id] || {};

      if (openSelectSaleDialog !== undefined) {
        newParams.openSelectSaleDialog = openSelectSaleDialog;
      }

      if (openSelectSaleConfigurationDialog !== undefined) {
        newParams.openSelectSaleConfigurationDialog =
          openSelectSaleConfigurationDialog;
      }

      if (saleConfiguration !== undefined) {
        newParams.saleConfiguration = saleConfiguration;
      }

      if (patientId !== undefined) {
        newParams.patientId = patientId;
      }

      if (saleId !== undefined) {
        newParams.saleId = saleId;
      }

      if (showSilenceDialog !== undefined) {
        newParams.showSilenceDialog = showSilenceDialog;
      }

      state.dataParams[id] = newParams;
    },

    clearDataParams: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!id) return;
      delete state.dataParams[id];
    }
  }
});

export const {
  setAiPromptDetails,
  clearAiPromptDetails,
  setDataParams,
  clearDataParams
} = wordProcessorSlice.actions;

const wordProcessorSelector = (state: RootState) => state.wordProcessor;

export const selectAiPrompts = createSelector(
  wordProcessorSelector,
  (wordProcessor) => wordProcessor.aiPrompts
);

export const selectAiPromptById = createSelector(
  [selectAiPrompts, (state: RootState, id: string) => id],
  (aiPrompts, id) => aiPrompts[id] || {}
);

export const selectDataParams = createSelector(
  wordProcessorSelector,
  (wordProcessor) => wordProcessor.dataParams
);

export const selectDataParamsById = createSelector(
  [selectDataParams, (state: RootState, id: string) => id],
  (dataParams, id) => dataParams[id] || {}
);

export default wordProcessorSlice.reducer;
